import React, { useMemo } from 'react';
import { i18n } from '@secretescapes/i18n';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { useTranslations } from '@utils/useTranslations';
import { RetryBox } from '@features/Sale/RetryBox/RetryBox';
import { deleteURLParams, getFinalRates } from '../AvailabilityPresentation.utils';
import { useSelectionState } from '../SelectionContext';
import { OfferCard } from './OfferCard';
import { type ExtendedOffer } from './Offers.interfaces';
import { Grid } from './Offers.styles';
import {
  getFlightsDescription,
  getInfoBoxType,
  getTravellersFromFacets,
  isBookable,
  showDisclaimer,
  sortOffers,
} from './Offers.utils';

interface Props {
  offers: ExtendedOffer[];
  onBookClick: (offerId: string) => void;
  openCalendar: (offerId: string) => void;
  isLoading: boolean;
  flightsSelected: boolean;
  hasNoFlights: boolean;
}

export const Offers = ({
  offers,
  onBookClick,
  openCalendar,
  isLoading,
  flightsSelected,
  hasNoFlights,
}: Props) => {
  const { territoryLocale, currency, isMobile } = useApplicationState();
  const { finalRates } = useAppState();
  const { travellersSelection } = useSelectionState();
  const { translations } = useTranslations();

  const determineInfoBox = (offer: ExtendedOffer) => {
    return getInfoBoxType(finalRates.show, offer, travellersSelection);
  };

  const sortedOffers = sortOffers(offers, determineInfoBox);

  const showPhotos = useMemo(() => offers.every(offer => offer?.room?.photos?.length), [offers]);

  if (isLoading) {
    return (
      <Skeleton
        numberOfCards={offers.length}
        showOfferCardButton={finalRates.show}
        showDiscount={!flightsSelected}
        showPhoto={showPhotos}
      />
    );
  }

  if (hasNoFlights) {
    return (
      <RetryBox
        retryFunction={() => {
          setURLParams('tripType', 'hotelOnly');
          deleteURLParams();
          window?.location?.reload();
        }}
        subtitle={i18n('sale.noflights.text', [], translations)}
        buttonText={i18n('sale.navigate.hotelonly', [], translations)}
        isMobile={isMobile}
      />
    );
  }

  return (
    <Grid>
      {sortedOffers.map(offer => {
        const { description, pricing } = offer;

        const rates = getFinalRates(
          offer.receipt,
          travellersSelection.rooms.length,
          currency,
          territoryLocale
        );

        const infoBoxType = determineInfoBox(offer);

        const showLabelDisclaimer = showDisclaimer(
          travellersSelection,
          offer.pricing?.packagePriceBasis
        );

        return (
          <OfferCard
            key={offer.id}
            offer={offer}
            isRefundableCheckInDate={offer.receipt?.isRefundable ?? undefined}
            isBookable={isBookable(finalRates.show, offer.receipt)}
            onBookClick={onBookClick}
            finalRate={rates?.finalRate ?? ''}
            finalRatePerRoomPerNight={rates?.finalRatePerRoomPerNight ?? ''}
            availableRoomCount={offer.receipt?.maxAvailability ?? undefined}
            openCalendar={openCalendar}
            infoBoxType={isLoading ? null : infoBoxType}
            description={
              flightsSelected
                ? getFlightsDescription(description ?? '', translations)
                : (description ?? '')
            }
            numberOfTravellers={
              offer.receipt
                ? finalRates.numberOfTravellers
                : getTravellersFromFacets(pricing?.packagePriceBasis)
            }
            numberOfNights={finalRates.numberOfNights}
            flightsSelected={flightsSelected}
            showPhotos={showPhotos}
            showLabelDisclaimer={showLabelDisclaimer}
            numberOfRooms={travellersSelection.rooms.length}
          />
        );
      })}
    </Grid>
  );
};

interface OffersSkeletonProps {
  numberOfCards: number;
  showOfferCardButton?: boolean;
  showDiscount: boolean;
  showPhoto: boolean;
}

const Skeleton = ({
  numberOfCards,
  showOfferCardButton,
  showDiscount,
  showPhoto,
}: OffersSkeletonProps) => {
  return (
    <Grid>
      {Array.from({ length: numberOfCards }, (_, index) => (
        <OfferCard.Skeleton
          key={`skeleton-${index}`}
          showButton={!!showOfferCardButton}
          showDiscount={showDiscount}
          showPhoto={showPhoto}
        />
      ))}
    </Grid>
  );
};

Offers.Skeleton = Skeleton;
